<template>
  <div :class="['login-layout', `theme-${theme}`, { 'dark-mode': isDark }]">

    <!-- Background image -->
    <div class="bg-image"></div>

    <!-- Overlay -->
    <div class="bg-overlay"></div>

    <!-- Left -->
    <div class="login-main">

      <!-- Login Card -->
      <div class="login-card">

        <!-- Logo -->
        <div class="card-logo">
          <img src="@/assets/images/login-logo.png" alt="Ogun State Seal" />
        </div>

        <!-- Title -->
        <div class="login-title">
          <h1 class="card-title">Forgot Password?</h1>
          <p class="card-sub-title">A temporary password will be sent to your email</p>
        </div>

        <!-- Error -->
        <div v-if="errorMessage" class="error-banner">
          {{ errorMessage }}
        </div>
        <!-- Success -->
        <div v-if="successMessage" class="success-message">
          {{ successMessage }}
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="card-form">

          <div class="form-group mb-4">
            <label for="email">Username</label>
            <input id="email" v-model="email" type="email" placeholder="Enter your Username" @input="clearErrorMessage"
              required />
          </div>

          <button type="submit" class="submit-btn mb-4" :disabled="isLoading">
            <span v-if="isLoading">
              <i class="fas fa-spinner fa-spin"></i> Submitting...
            </span>
            <span v-else>
              Submit
            </span>
          </button>

        </form>

        <div>
          <router-link to="/login" class="back-to-login d-flex align-items-center justify-content-center">
            <span class="material-symbols-outlined me-1">
              arrow_left_alt
            </span>
            <p class="mb-0">Back to Login</p>
          </router-link>
        </div>

        <!-- Footer -->
        <p class="card-footer">© 2025-{{ currentYear }} Powered by Tinovo Technology Services.</p>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'
import '@/assets/css/login.css'
// import Cookies from 'js-cookie'
// import { useRouter } from 'vue-router'
// import { useUserStore } from '@/stores/user'
import { useTheme } from '@/composables/useTheme'
const { theme } = useTheme()
const isDark = ref(localStorage.getItem('theme') === 'dark')

onMounted(() => {
  document.documentElement.setAttribute('data-bs-theme', isDark.value ? 'dark' : 'light')
})

// 📦 Reactive State
const email = ref('')
const errorMessage = ref('')
const successMessage = ref('')
const isLoading = ref(false)
// const router = useRouter()
// const userStore = useUserStore()


const currentYear = new Date().getFullYear()

// 💡 Methods
function clearErrorMessage() {
  errorMessage.value = ''
  successMessage.value = ''
}

async function handleSubmit() {
  isLoading.value = true
  errorMessage.value = ''
  successMessage.value = ''
  const root_api_url = process.env.VUE_APP_API_ROOT_URL

  try {
    const result = await axios.post(`${root_api_url}/auth/forgot-password`, {
      changePassword: true,
      changePasswordInd: 1,
      email: email.value,
    })

    // console.log("inside forgot password data", result.data)

    if (result.status === 200 && result.data.errorMessage !== "Unknown User") {
      successMessage.value = "Password reset email sent successfully."

    }
  } catch (err) {
    if (err?.response?.status === 417) {
      errorMessage.value = err.response.data.errorMessage
    } else if (err?.response?.status === 503) {
      errorMessage.value = err.response.data.error
    } else {
      errorMessage.value = 'Unexpected error occurred, kindly contact administrator'
    }
  } finally {
    isLoading.value = false
  }
}
</script>


<style scoped>
/* ─── Layout ─────────────────────────────────────── */
.login-layout {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ─── Background image ────────────────────────────── */
.bg-image {
  position: absolute;
  inset: 0;
  background-image: url('@/assets/images/login-image-removebg2.png');
  background-size: cover;
  background-position: center;
  z-index: 0;
}

.bg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  z-index: 1;
}

/* ─── Card ────────────────────────────────────────── */
.login-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 460px;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-radius: 16px;
  padding: 2.5rem 2.75rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.18);
}

/* ─── Logo ────────────────────────────────────────── */
.card-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 1rem;
}

.card-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ─── Title ───────────────────────────────────────── */
.card-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--color);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

.card-sub-title {
  font-size: 1rem;
  font-weight: 500;
  color: var(--color);
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

/* ─── Error ───────────────────────────────────────── */
.error-banner {
  width: 100%;
  background: #fff0f0;
  border: 1px solid #f5c6c6;
  color: #c0392b;
  font-size: 0.875rem;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
}

/* ─── Form ────────────────────────────────────────── */
.card-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.4rem;
}

.form-group input {
  width: 100%;
  padding: 0.65rem 1rem;
  border: 1.5px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1a1a2e;
  background: rgba(255, 255, 255, 0.7);
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #a0aab4;
}

.form-group input:focus {
  border-color: var(--btn-alt);
  background: #fff;
}

/* ─── Button ──────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 0.75rem;
  background: var(--btn-primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s, transform 0.1s;
}

.submit-btn:hover:not(:disabled) {
  background: var(--btn-primary-hover);
}

.submit-btn:active:not(:disabled) {
  transform: scale(0.99);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* ─── Footer ──────────────────────────────────────── */
.card-footer {
  margin-top: 1.5rem;
  font-size: 0.78rem;
  color: #7f8c8d;
  text-align: center;
}

/* ─── Dark mode ───────────────────────────────────── */
.dark-mode .login-card {
  background: rgba(20, 24, 36, 0.65);
}

.dark-mode .card-title {
  color: #f0f4f8;
}

.dark-mode .form-group label {
  color: #cdd5df;
}

.dark-mode .form-group input {
  background: rgba(255, 255, 255, 0.08);
  border-color: #3a4556;
  color: #f0f4f8;
}

.dark-mode .form-group input:focus {
  border-color: #27ae60;
  background: rgba(255, 255, 255, 0.12);
}

.dark-mode .card-footer {
  color: #6b7a8d;
}

/* ─── Responsive ──────────────────────────────────── */
@media (max-width: 520px) {
  .login-card {
    margin: 1rem;
    padding: 2rem 1.5rem 1.5rem;
  }

  .card-title {
    font-size: 1.1rem;
  }
}

.error-message {
  color: red;
  font-size: 16px;
}

.success-message {
  color: green;
  font-size: 16px;
}

.sign-in-btn[disabled] {
  opacity: 0.7;
  cursor: not-allowed;
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.back-to-login {
  color: var(--color);
  text-decoration: none;
}

.back-to-login-link { 
    color: var(--color);
}

.back-to-login:hover {
  color: var(--hover);
}
</style>
